import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import { queryClient } from '@/services/queryClient';
import LoginPage from '@/pages/LoginPage';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import RestaurantsPage from '@/pages/RestaurantsPage';
import FloorManagementPage from '@/pages/FloorManagementPage';
import BookingConfigPage from '@/pages/BookingConfigPage';
import TerminalsPage from '@/pages/TerminalsPage';
import MenuManagementPage from '@/pages/MenuManagementPage';
import UsersPage from '@/pages/UsersPage';
import RolesPage from '@/pages/RolesPage';
import TableLayoutPage from '@/pages/TableLayoutPage';
import GroupsPage from '@/pages/GroupsPage';
import GroupDetailPage from '@/pages/GroupDetailPage';
import AIMenuUploadPage from '@/pages/AIMenuUploadPage';
import SyncMonitoringPage from '@/pages/SyncMonitoringPage';
import BillingPage from '@/pages/BillingPage';
import PlansPage from '@/pages/PlansPage';
import InvoicesPage from '@/pages/InvoicesPage';
import PaymentMethodsPage from '@/pages/PaymentMethodsPage';
import AuditLogPage from '@/pages/AuditLogPage';
import ErrorLogPage from '@/pages/ErrorLogPage';
import InventoryPage from '@/pages/InventoryPage';
import SalesReportPage from '@/pages/reports/SalesReportPage';
import MenuReportPage from '@/pages/reports/MenuReportPage';
import OperationsReportPage from '@/pages/reports/OperationsReportPage';
import ForbiddenPage from '@/pages/ForbiddenPage';
import { RequirePermission } from '@/components/auth/RequirePermission';
import { DefaultLandingRoute } from '@/components/auth/DefaultLandingRoute';
import { PERM } from '@/constants/permissions';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isHydrating = useAuthStore((state) => state.isHydrating);
  const location = useLocation();

  // Hold off rendering while the boot-time /me hydrate is in flight, rather than
  // flashing the dashboard with stale/empty permissions first (M1 fix).
  if (isHydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <svg
          className="animate-spin h-8 w-8 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the originally requested URL so LoginPage can return here afterward (H1 fix).
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Redirects an already-authenticated user away from /login instead of letting
// them resubmit the form and create a redundant session (C1 fix).
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  useEffect(() => {
    const state = useAuthStore.getState();
    // If the user has a persisted token but permissions/scope haven't been populated,
    // hydrate from /me. This covers the page-refresh case where Zustand persist
    // restored the token but the /me cache (permissions Set, scope) is empty.
    if (state.isAuthenticated && state.permissions.size === 0) {
      useAuthStore.setState({ isHydrating: true });
      apiClient
        .getMe()
        .then((me) => {
          state.setSession(me);
          useAuthStore.setState({ isHydrating: false });
        })
        .catch((err) => {
          console.warn('Boot-time /me hydrate failed; clearing auth', err);
          // Stale or revoked token — force re-login.
          useAuthStore.setState({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            permissions: new Set<string>(),
            scope: null,
            isHydrating: false,
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Forbidden page must be reachable by any authenticated user. */}
            <Route path="forbidden" element={<ForbiddenPage />} />

            <Route index element={<DefaultLandingRoute />} />
            <Route
              path="restaurants"
              element={
                <RequirePermission name={PERM.RESTAURANT_READ}>
                  <RestaurantsPage />
                </RequirePermission>
              }
            />
            <Route
              path="restaurants/:id/tables"
              element={
                <RequirePermission name={PERM.FLOOR_READ}>
                  <FloorManagementPage />
                </RequirePermission>
              }
            />
            {/* Legacy whole-restaurant editor URL — TableLayoutPage redirects to the
                floors tab when no floorId is present (kept so old links don't 404) */}
            <Route
              path="restaurants/:id/tables/layout"
              element={
                <RequirePermission name={PERM.FLOOR_READ}>
                  <TableLayoutPage />
                </RequirePermission>
              }
            />
            <Route
              path="restaurants/:id/tables/layout/:floorId"
              element={
                <RequirePermission name={PERM.FLOOR_READ}>
                  <TableLayoutPage />
                </RequirePermission>
              }
            />
            <Route
              path="restaurants/:id/booking"
              element={
                <RequirePermission name={PERM.BOOKING_READ}>
                  <BookingConfigPage />
                </RequirePermission>
              }
            />
            <Route
              path="restaurants/:id/terminals"
              element={
                <RequirePermission name={PERM.TERMINAL_READ}>
                  <TerminalsPage />
                </RequirePermission>
              }
            />
            <Route
              path="restaurants/:id/menu"
              element={
                <RequirePermission name={PERM.MENU_READ}>
                  <MenuManagementPage />
                </RequirePermission>
              }
            />
            <Route
              path="restaurants/:restaurantId/menu/ai-upload"
              element={
                <RequirePermission name={PERM.AI_MENU_UPLOAD}>
                  <AIMenuUploadPage />
                </RequirePermission>
              }
            />
            <Route
              path="users"
              element={
                <RequirePermission name={PERM.USER_READ}>
                  <UsersPage />
                </RequirePermission>
              }
            />
            <Route
              path="roles"
              element={
                <RequirePermission name={PERM.ROLE_READ}>
                  <RolesPage />
                </RequirePermission>
              }
            />
            <Route path="analytics" element={<Navigate to="/reports/sales" replace />} />
            <Route
              path="groups"
              element={
                <RequirePermission name={PERM.GROUP_READ}>
                  <GroupsPage />
                </RequirePermission>
              }
            />
            <Route
              path="groups/:groupId"
              element={
                <RequirePermission name={PERM.GROUP_READ}>
                  <GroupDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="sync"
              element={
                <RequirePermission name={PERM.SYNC_READ}>
                  <SyncMonitoringPage />
                </RequirePermission>
              }
            />
            <Route
              path="billing"
              element={
                <RequirePermission name={PERM.BILLING_READ}>
                  <BillingPage />
                </RequirePermission>
              }
            />
            <Route
              path="billing/plans"
              element={
                <RequirePermission name={PERM.BILLING_READ}>
                  <PlansPage />
                </RequirePermission>
              }
            />
            <Route
              path="billing/invoices"
              element={
                <RequirePermission name={PERM.BILLING_READ}>
                  <InvoicesPage />
                </RequirePermission>
              }
            />
            <Route
              path="billing/payment-methods"
              element={
                <RequirePermission name={PERM.BILLING_READ}>
                  <PaymentMethodsPage />
                </RequirePermission>
              }
            />
            <Route
              path="audit"
              element={
                <RequirePermission name={PERM.AUDIT_READ}>
                  <AuditLogPage />
                </RequirePermission>
              }
            />
            <Route
              path="errors"
              element={
                <RequirePermission name={PERM.ERRORS_READ}>
                  <ErrorLogPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory"
              element={
                <RequirePermission name={PERM.INVENTORY_READ}>
                  <InventoryPage />
                </RequirePermission>
              }
            />
            <Route path="reports" element={<Navigate to="/reports/sales" replace />} />
            <Route
              path="reports/sales"
              element={
                <RequirePermission name={PERM.REPORT_READ}>
                  <SalesReportPage />
                </RequirePermission>
              }
            />
            <Route
              path="reports/menu"
              element={
                <RequirePermission name={PERM.REPORT_READ}>
                  <MenuReportPage />
                </RequirePermission>
              }
            />
            <Route
              path="reports/operations"
              element={
                <RequirePermission name={PERM.REPORT_READ}>
                  <OperationsReportPage />
                </RequirePermission>
              }
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
